import { useEffect, useState } from "react";
import AbsenModal from "../KonfirmasiLokasiModal";
import CameraModal from "../OpenCamera";
import Swal from "sweetalert2";

interface PhotoCameraProps {
  mode?: "dalam" | "luar" | null;
  pgw_id: string;
  kelainan: string;
  gps_lat: number;
  gps_long: number;
  is_kantor: number;
  is_masuk: number;
  tipe_absen: number;
}

interface KelainanOption {
  id: string;
  nama_kelainan: string;
}

type StepType = "kelainan" | "selfie" | "around" | "document" | null;

type UploadResult =
  | { success: true }
  | {
    success: false;
    reason: "CANNOT_ATTEDANCE_OUTSIDE" | "SERVER_ERROR" | "NETWORK_ERROR";
    message?: string;
  };

// Toast ringan, auto-close, ga blocking - buat feedback antar-step aja
const Toast = Swal.mixin({
  toast: true,
  position: "top-end",
  showConfirmButton: false,
  timer: 1200,
  timerProgressBar: true,
});

const PhotoCamera: React.FC<PhotoCameraProps> = ({
  mode,
  pgw_id,
  kelainan,
  gps_lat,
  gps_long,
  is_kantor,
  is_masuk,
  tipe_absen,
}) => {
  // Step awal tergantung mode: is_kantor 0 -> mulai dari pilih kelainan,
  // is_kantor 1 -> langsung ke selfie.
  const getInitialStep = (): StepType =>
    mode ? (is_kantor === 0 ? "kelainan" : "selfie") : null;

  const [isAbsenModalOpen, setIsAbsenModalOpen] = useState<boolean>(!mode);
  const [currentStep, setCurrentStep] = useState<StepType>(getInitialStep());

  // Satu-satunya sumber kebenaran soal kamera tampil/nggak, dipisah dari currentStep.
  const [isCameraOpen, setIsCameraOpen] = useState<boolean>(
    getInitialStep() !== null && getInitialStep() !== "kelainan"
  );
  const [capturedImages, setCapturedImages] = useState<Record<string, string | File>>({});
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [kelainanList, setKelainanList] = useState<KelainanOption[]>([]);
  const [selectedKelainan, setSelectedKelainan] = useState<string>("");

  // Fetch the list of kelainan from the API
  useEffect(() => {
    const fetchKelainan = async () => {
      try {
        const response = await fetch(
          "https://tarum-tools.aurorasystem.co.id/kepegawaian-api/ref_kelainan.php"
        );
        const data = await response.json();

        if (data?.data) {
          setKelainanList(data.data);
        } else {
          console.error("Unexpected response structure:", data);
        }
      } catch (error) {
        console.error("Failed to fetch kelainan:", error);
      }
    };

    fetchKelainan();
  }, []);

  // Buka kamera untuk step tertentu
  const openCameraForStep = (step: StepType) => {
    setCurrentStep(step);
    setIsCameraOpen(step !== null && step !== "kelainan");
  };

  // Tutup kamera tanpa ubah currentStep (dipakai sesaat sebelum modal hasil muncul)
  const closeCamera = () => {
    setIsCameraOpen(false);
  };

  // Tutup SEMUANYA total - gak buka kelainan select, gak buka kamera lagi.
  // Dipakai khusus buat penolakan mutlak (mis. CANNOT_ATTEDANCE_OUTSIDE) yang
  // gak akan pernah berhasil walau di-retry di flow ini.
  const closeEverything = () => {
    setCapturedImages({});
    setSelectedKelainan("");
    setCurrentStep(null);
    setIsCameraOpen(false);
  };

  // Balik ke kondisi paling awal buat kegagalan yang LAYAK di-retry
  // (network/server error teknis) - foto dihapus, step di-reset sesuai mode.
  const resetToInitial = () => {
    setCapturedImages({});
    setSelectedKelainan("");
    openCameraForStep(getInitialStep());
  };

  // Dipanggil kalau upload SUKSES - flow selesai, semua tertutup.
  const finishFlow = () => {
    setCapturedImages({});
    setCurrentStep(null);
    setIsCameraOpen(false);
  };

  // Mutusin recovery step yang tepat berdasarkan alasan kegagalan.
  const handleUploadFailure = (result: Extract<UploadResult, { success: false }>) => {
    if (result.reason === "CANNOT_ATTEDANCE_OUTSIDE") {
      // Penolakan mutlak dari server, retry di flow ini percuma.
      closeEverything();
    } else {
      // Gagal teknis (network/server) - layak di-retry dari awal.
      resetToInitial();
    }
  };

  // Cuma hit API + parse hasil. Ga nembak Swal disini,
  // biar modal hasil cuma muncul SEKALI (diatur di showUploadResult).
  const uploadPhotos = async (formData: FormData): Promise<UploadResult> => {
    try {
      formData.append("pgw_id", pgw_id);
      formData.append("gps_lat", gps_lat.toString());
      formData.append("gps_long", gps_long.toString());
      formData.append("is_masuk", is_masuk.toString());
      formData.append("is_kantor", is_kantor.toString());
      formData.append("tipe_absen", tipe_absen.toString());
      formData.append("kelainan", selectedKelainan);
      formData.append("is_pwa", "1");

      const response = await fetch(
        "https://tarum-tools.aurorasystem.co.id/kepegawaian-api/upload_absensi.php",
        {
          method: "POST",
          body: formData,
        }
      );

      const result = await response.json();

      if (result.error === "CANNOT_ATTEDANCE_OUTSIDE") {
        return { success: false, reason: "CANNOT_ATTEDANCE_OUTSIDE" };
      }

      if (result.status === "403" || result.error) {
        return {
          success: false,
          reason: "SERVER_ERROR",
          message: result.message || result.keterangan,
        };
      }

      return { success: true };
    } catch (error) {
      console.error("Error uploading photos:", error);
      return { success: false, reason: "NETWORK_ERROR" };
    }
  };

  // Nunggu (await) sampe user nutup modalnya, baru resolve.
  const showUploadResult = async (result: UploadResult): Promise<void> => {
    if (result.success) {
      await Swal.fire("Berhasil", "Absen berhasil!", "success");
      setTimeout(() => {
        window.location.reload();
      }, 1000)
      return;
    }

    switch (result.reason) {
      case "CANNOT_ATTEDANCE_OUTSIDE":
        await Swal.fire(
          "Tidak Diizinkan",
          "Tidak diizinkan absen diluar kantor!",
          "error"
        );
        break;
      case "NETWORK_ERROR":
        await Swal.fire(
          "Gagal",
          "Gagal mengupload foto. Cek koneksi anda dan coba lagi.",
          "error"
        );
        break;
      default:
        await Swal.fire(
          "Gagal",
          result.message || "Upload gagal, silakan coba lagi.",
          "error"
        );
    }
  };

  // Handle capturing and uploading images
  const handleCapture = async (imageData: string): Promise<void> => {
    if (!currentStep) return;

    const stepBeingProcessed = currentStep;

    try {
      const blob = await (await fetch(imageData)).blob();
      const file = new File([blob], `${stepBeingProcessed}.jpg`, {
        type: "image/jpeg",
      });

      // Save captured image locally
      setCapturedImages((prev) => ({
        ...prev,
        [stepBeingProcessed]: file,
      }));

      // Check if in office (is_kantor = 1)
      if (is_kantor === 1) {
        if (stepBeingProcessed === "selfie") {
          // Tutup kamera DULU sebelum modal hasil muncul, biar ga tabrakan/numpuk.
          closeCamera();
          setIsUploading(true);

          const formData = new FormData();
          formData.append("file_photo_wajah", file);

          const result = await uploadPhotos(formData);
          setIsUploading(false);
          await showUploadResult(result);

          if (result.success) {
            finishFlow();
          } else {
            handleUploadFailure(result);
          }
        }
      } else {
        // If outside the office (is_kantor = 0), proceed through all steps
        if (stepBeingProcessed === "document") {
          if (!capturedImages["selfie"] || !capturedImages["around"]) {
            Swal.fire(
              "Belum Lengkap",
              "Foto selfie dan sekitar lokasi belum lengkap. Ulangi dari awal ya.",
              "warning"
            );
            resetToInitial();
            return;
          }

          // Tutup kamera DULU sebelum modal hasil muncul.
          closeCamera();
          setIsUploading(true);

          const formData = new FormData();
          formData.append(
            "file_photo_wajah",
            capturedImages["selfie"] as File
          );
          formData.append(
            "file_photo_lokasi",
            capturedImages["around"] as File
          );
          formData.append("file_photo_surat", file);

          const result = await uploadPhotos(formData);
          setIsUploading(false);
          await showUploadResult(result);

          if (result.success) {
            finishFlow();
          } else {
            handleUploadFailure(result);
          }
        } else {
          // kasih feedback ringan tanpa blocking flow
          Toast.fire({
            icon: "success",
            title: `${getStepTitle(stepBeingProcessed)} tersimpan`,
          });

          // Proceed to the next step, kamera tetap terbuka
          openCameraForStep(
            stepBeingProcessed === "selfie"
              ? "around"
              : stepBeingProcessed === "around"
                ? "document"
                : null
          );
        }
      }
    } catch (error) {
      // Ini error teknis (gagal proses gambar), bukan error validasi server.
      // Kamera dibuka lagi di step yang sama biar user bisa coba ambil ulang.
      console.error("Error during capture or upload:", error);
      setIsUploading(false);
      openCameraForStep(stepBeingProcessed);
      Swal.fire("Gagal", "Gagal memproses foto. Silakan coba lagi.", "error");
    }
  };

  // Helper function to get step title
  const getStepTitle = (step: string): string => {
    switch (step) {
      case "selfie":
        return "Foto selfie";
      case "around":
        return "Foto sekitar lokasi";
      case "document":
        return "Foto dokumen";
      default:
        return "Foto";
    }
  };

  // Update selected kelainan
  useEffect(() => {
    if (kelainan) {
      setSelectedKelainan(kelainan);
      openCameraForStep("selfie");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kelainan]);

  return (
    <div>
      <AbsenModal
        isOpen={isAbsenModalOpen}
        onClose={() => setIsAbsenModalOpen(false)}
        onSelect={() => {
          setIsAbsenModalOpen(false);
          openCameraForStep("selfie");
        }}
      />

      {currentStep === "kelainan" && (
        <div className="kelainan-select">
          <h3 className="mb-2">Pilih Kelainan</h3>
          <select
            className="border rounded px-3 py-2 w-full"
            value={selectedKelainan}
            onChange={(e) => setSelectedKelainan(e.target.value)}
          >
            <option value="">-- Pilih --</option>
            {kelainanList.map((item) => (
              <option key={item.id} value={item.nama_kelainan}>
                {item.nama_kelainan}
              </option>
            ))}
          </select>
          <button
            className="mt-4 bg-blue-500 text-white px-4 py-2 rounded"
            onClick={() => {
              if (selectedKelainan) {
                openCameraForStep("selfie");
              } else {
                Swal.fire(
                  "Error",
                  "Pilih kelainan dulu sebelum lanjut.",
                  "error"
                );
              }
            }}
          >
            Lanjut
          </button>
        </div>
      )}

      {isCameraOpen && currentStep && currentStep !== "kelainan" && (
        <CameraModal
          key={currentStep}
          isOpen={isCameraOpen}
          onCapture={handleCapture}
          currentStep={currentStep}
        />
      )}

      {isUploading && <p>Mengupload foto...</p>}
    </div>
  );
};

export default PhotoCamera;